"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import GoogleMaterialIcon from "@/components/ui/GoogleMaterialIcon";

// REQUIREMENTS SUMMARY:
// - Check whether the deployed CheerVue app version changed while a user's browser tab stayed open.
// - Show a plain-English refresh prompt for non-technical gym users without interrupting their current task.

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const SNOOZE_DURATION_MS = 30 * 60 * 1000;
const SNOOZE_STORAGE_KEY = "cheervue-update-snoozed-until";

type VersionResponse = {
  version?: string;
};

function getSnoozedUntil() {
  return Number(window.sessionStorage.getItem(SNOOZE_STORAGE_KEY) || "0");
}

function snoozeUpdatePrompt() {
  window.sessionStorage.setItem(
    SNOOZE_STORAGE_KEY,
    String(Date.now() + SNOOZE_DURATION_MS)
  );
}

async function fetchCurrentVersion() {
  const response = await fetch(`/api/app-version?ts=${Date.now()}`, {
    cache: "no-store",
  });

  if (!response.ok) {
    return null;
  }

  const data = (await response.json()) as VersionResponse;
  return data.version || null;
}

export default function AppUpdatePrompt() {
  const { snoozeUpdate, updateAvailable } = useAppUpdateAvailable();

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleSnooze = () => {
    snoozeUpdate();
  };

  if (!updateAvailable) {
    return null;
  }

  return <UpdatePromptCard onRefresh={handleRefresh} onSnooze={handleSnooze} />;
}

function useAppUpdateAvailable() {
  const initialVersionRef = useRef<string | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  const checkForUpdate = useCallback(async () => {
    try {
      if (getSnoozedUntil() > Date.now()) {
        return;
      }

      const currentVersion = await fetchCurrentVersion();
      if (!currentVersion) {
        return;
      }

      if (!initialVersionRef.current) {
        initialVersionRef.current = currentVersion;
        return;
      }

      if (currentVersion !== initialVersionRef.current) {
        setUpdateAvailable(true);
      }
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.warn("Unable to check for a CheerVue update:", error);
      }
    }
  }, []);

  useEffect(() => {
    checkForUpdate();

    const intervalId = window.setInterval(checkForUpdate, CHECK_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkForUpdate();
      }
    };

    window.addEventListener("focus", checkForUpdate);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", checkForUpdate);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [checkForUpdate]);

  const snoozeUpdate = useCallback(() => {
    snoozeUpdatePrompt();
    setUpdateAvailable(false);
  }, []);

  return { snoozeUpdate, updateAvailable };
}

function UpdatePromptCard({
  onRefresh,
  onSnooze,
}: {
  onRefresh: () => void;
  onSnooze: () => void;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-4 bottom-4 z-[9999998] mx-auto max-w-[520px] rounded-md border border-brand-200 bg-white p-4 shadow-theme-lg dark:border-brand-500/30 dark:bg-gray-900 sm:left-auto sm:right-4 sm:mx-0"
    >
      <div className="flex gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-500 dark:bg-brand-500/15">
          <GoogleMaterialIcon name="system_update_alt" size="md" />
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">
            A new CheerVue update is ready
          </p>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            Refresh when you have a moment to get the latest fixes and improvements.
          </p>

          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onRefresh}
              className="inline-flex items-center gap-2 rounded-md bg-brand-500 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
            >
              <GoogleMaterialIcon name="refresh" size="sm" />
              Refresh now
            </button>
            <button
              type="button"
              onClick={onSnooze}
              className="inline-flex items-center rounded-md border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-white/[0.04] dark:focus:ring-offset-gray-900"
            >
              Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
